import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';

/* ── Estilos inline (consistentes con el CRM) ─────────────────────────────── */
const S = {
  page:     { padding: '2rem', maxWidth: 1100, margin: '0 auto', fontFamily: 'Georgia, serif' },
  h1:       { fontSize: '1.6rem', color: '#f2ead8', marginBottom: '1.5rem' },
  tabs:     { display: 'flex', gap: 4, marginBottom: '1.5rem', borderBottom: '1px solid #2d2926' },
  tab:      (a) => ({
    padding: '10px 20px', cursor: 'pointer', fontSize: '.85rem', fontWeight: 600,
    color: a ? '#5a7a50' : '#8a8070', borderBottom: a ? '2px solid #5a7a50' : '2px solid transparent',
    background: 'none', border: 'none', borderBottomWidth: 2, borderBottomStyle: 'solid',
    borderBottomColor: a ? '#5a7a50' : 'transparent', transition: 'all .2s',
  }),
  card:     { background: '#1e1b17', border: '1px solid #2d2926', borderRadius: 8, padding: '1rem', marginBottom: '.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  badge:    (c) => ({ fontSize: '.7rem', padding: '3px 10px', borderRadius: 20, fontWeight: 600, background: c === 'green' ? '#2d3e28' : c === 'amber' ? '#3e3520' : '#2d2926', color: c === 'green' ? '#7ab87a' : c === 'amber' ? '#d4a035' : '#8a8070' }),
  btn:      (c) => ({ padding: '8px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: '.8rem', fontWeight: 600, color: '#f2ead8', background: c === 'green' ? '#5a7a50' : c === 'red' ? '#8b3a3a' : '#3a3530', transition: 'opacity .2s' }),
  input:    { width: '100%', padding: '10px 14px', borderRadius: 6, border: '1px solid #2d2926', background: '#161310', color: '#f2ead8', fontSize: '.85rem', fontFamily: 'Georgia, serif', outline: 'none' },
  label:    { fontSize: '.75rem', color: '#8a8070', fontWeight: 600, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.05em' },
  muted:    { fontSize: '.8rem', color: '#8a8070' },
  // Chat
  chatWrap: { display: 'flex', gap: '1rem', height: 'calc(100vh - 200px)', minHeight: 400 },
  contacts: { width: 280, flexShrink: 0, background: '#1e1b17', border: '1px solid #2d2926', borderRadius: 8, overflow: 'auto' },
  contact:  (a) => ({ padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #2d2926', background: a ? '#2d2926' : 'transparent', transition: 'background .15s' }),
  chatArea: { flex: 1, display: 'flex', flexDirection: 'column', background: '#1e1b17', border: '1px solid #2d2926', borderRadius: 8, overflow: 'hidden' },
  chatMsgs: { flex: 1, overflow: 'auto', padding: '1rem', display: 'flex', flexDirection: 'column', gap: 8 },
  bubble:   (out) => ({
    maxWidth: '70%', padding: '10px 14px', borderRadius: 12, fontSize: '.85rem', lineHeight: 1.5, wordWrap: 'break-word',
    alignSelf: out ? 'flex-end' : 'flex-start',
    background: out ? '#2d3e28' : '#2d2926',
    color: out ? '#c8e6c0' : '#f2ead8',
    borderBottomRightRadius: out ? 2 : 12,
    borderBottomLeftRadius:  out ? 12 : 2,
  }),
  bubbleTs: { fontSize: '.65rem', color: '#6a6050', marginTop: 2, textAlign: 'right' },
  chatInput:{ display: 'flex', gap: 8, padding: '12px', borderTop: '1px solid #2d2926', background: '#161310' },
  locked:   { padding: '14px', textAlign: 'center', background: '#2a2015', borderTop: '1px solid #3e3520', fontSize: '.8rem', color: '#d4a035' },
  stats:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem', marginBottom: '1.5rem' },
  statCard: { background: '#1e1b17', border: '1px solid #2d2926', borderRadius: 8, padding: '1rem', textAlign: 'center' },
  statVal:  { fontSize: '1.8rem', fontWeight: 700, color: '#f2ead8' },
  statLbl:  { fontSize: '.7rem', color: '#8a8070', marginTop: 4, textTransform: 'uppercase', letterSpacing: '.05em' },
};

export default function AdminWhatsApp() {
  const { token } = useAuth();
  const [tab, setTab]         = useState('leads');  // leads | chat
  const [leads, setLeads]     = useState([]);
  const [loading, setLoading] = useState(true);

  // Chat state
  const [clients, setClients]       = useState([]);
  const [selected, setSelected]     = useState(null);  // phone
  const [messages, setMessages]     = useState([]);
  const [session, setSession]       = useState(null);
  const [msgInput, setMsgInput]     = useState('');
  const [sending, setSending]       = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const msgsEndRef = useRef(null);

  // Convert modal
  const [convertPhone, setConvertPhone] = useState(null);
  const [convertName, setConvertName]   = useState('');
  const [convertEmail, setConvertEmail] = useState('');
  const [convertNotes, setConvertNotes] = useState('');
  const [converting, setConverting]     = useState(false);

  // ── Cargar leads ─────────────────────────────────────────────
  const loadLeads = useCallback(async () => {
    try {
      const data = await api.waGetLeads(token);
      setLeads(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  // ── Cargar clientes con WA (para pestaña chat) ──────────────
  const loadClients = useCallback(async () => {
    // Por ahora, obtenemos leads + clientes. Los clientes se obtienen de los mensajes unicos
    // Simplificacion: usar leads como contactos de la pestaña leads, y para chat
    // necesitariamos los clientes. Por ahora mostramos leads en chat tambien.
    // En futuro: endpoint dedicado para clientes con WA
    try {
      const data = await api.waGetLeads(token);
      setClients(data);
    } catch (e) { console.error(e); }
  }, [token]);

  useEffect(() => {
    loadLeads();
    loadClients();
  }, [loadLeads, loadClients]);

  // ── Cargar mensajes de un contacto ────────────────────────────
  const selectContact = async (phone) => {
    setSelected(phone);
    setLoadingMsgs(true);
    try {
      const [msgs, sess] = await Promise.all([
        api.waGetMessages(token, phone),
        api.waGetSession(token, phone),
      ]);
      setMessages(msgs);
      setSession(sess);
    } catch (e) { console.error(e); }
    setLoadingMsgs(false);
  };

  useEffect(() => {
    if (msgsEndRef.current) msgsEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Enviar mensaje ────────────────────────────────────────────
  const handleSend = async () => {
    if (!msgInput.trim() || !selected) return;
    setSending(true);
    try {
      await api.waSendMessage(token, selected, msgInput.trim());
      setMsgInput('');
      // Recargar mensajes
      const msgs = await api.waGetMessages(token, selected);
      setMessages(msgs);
    } catch (e) {
      alert('Error enviando: ' + e.message);
    }
    setSending(false);
  };

  // ── Convertir lead ────────────────────────────────────────────
  const handleConvert = async () => {
    if (!convertPhone) return;
    setConverting(true);
    try {
      await api.waConvertLead(token, convertPhone, {
        name: convertName, email: convertEmail, notes: convertNotes,
      });
      setConvertPhone(null);
      setConvertName(''); setConvertEmail(''); setConvertNotes('');
      loadLeads();
      loadClients();
    } catch (e) {
      alert('Error: ' + e.message);
    }
    setConverting(false);
  };

  const openConvert = (lead) => {
    setConvertPhone(lead.phone);
    setConvertName(lead.waName || '');
    setConvertEmail('');
    setConvertNotes('');
  };

  const fmtDate = (ts) => {
    if (!ts) return '';
    const d = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
    return d.toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const fmtTime = (ts) => {
    if (!ts) return '';
    const d = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
    return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div style={S.page}>
      <h1 style={S.h1}>WhatsApp</h1>

      {/* Stats */}
      <div style={S.stats}>
        <div style={S.statCard}>
          <div style={S.statVal}>{leads.length}</div>
          <div style={S.statLbl}>Nuevos Leads</div>
        </div>
        <div style={S.statCard}>
          <div style={S.statVal}>{clients.length}</div>
          <div style={S.statLbl}>Conversaciones</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={S.tabs}>
        <button style={S.tab(tab === 'leads')} onClick={() => setTab('leads')}>
          Nuevos leads {leads.length > 0 && `(${leads.length})`}
        </button>
        <button style={S.tab(tab === 'chat')} onClick={() => setTab('chat')}>
          Chat
        </button>
      </div>

      {/* ── TAB: Leads ───────────────────────────────────────────── */}
      {tab === 'leads' && (
        <div>
          {loading ? (
            <p style={S.muted}>Cargando leads...</p>
          ) : leads.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem 0' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '.5rem' }}>📱</div>
              <p style={S.muted}>No hay leads nuevos por WhatsApp</p>
              <p style={{ ...S.muted, fontSize: '.75rem', marginTop: 4 }}>
                Cuando alguien escriba por WhatsApp y no sea cliente, aparecera aqui
              </p>
            </div>
          ) : (
            leads.map(lead => (
              <div key={lead.phone} style={S.card}>
                <div>
                  <div style={{ color: '#f2ead8', fontWeight: 600, fontSize: '.9rem' }}>
                    {lead.waName || 'Sin nombre'}{' '}
                    <span style={{ ...S.muted, fontWeight: 400 }}>+{lead.phone}</span>
                  </div>
                  <div style={{ ...S.muted, marginTop: 4 }}>
                    "{lead.lastMessage?.substring(0, 80)}{lead.lastMessage?.length > 80 ? '...' : ''}"
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center' }}>
                    <span style={S.badge('amber')}>{lead.messageCount} msg</span>
                    <span style={{ fontSize: '.7rem', color: '#6a6050' }}>{fmtDate(lead.lastMessageAt)}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={S.btn('green')} onClick={() => openConvert(lead)}>
                    Anadir como Cliente
                  </button>
                  <button style={S.btn()} onClick={() => { setTab('chat'); selectContact(lead.phone); }}>
                    Ver chat
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ── TAB: Chat ────────────────────────────────────────────── */}
      {tab === 'chat' && (
        <div style={S.chatWrap}>
          {/* Lista de contactos */}
          <div style={S.contacts}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #2d2926' }}>
              <div style={{ fontSize: '.75rem', color: '#8a8070', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' }}>
                Contactos
              </div>
            </div>
            {leads.map(c => (
              <div key={c.phone} style={S.contact(selected === c.phone)} onClick={() => selectContact(c.phone)}>
                <div style={{ color: '#f2ead8', fontSize: '.85rem', fontWeight: 600 }}>
                  {c.waName || '+' + c.phone}
                </div>
                <div style={{ fontSize: '.7rem', color: '#6a6050', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {c.lastMessage?.substring(0, 40)}
                </div>
              </div>
            ))}
            {leads.length === 0 && (
              <div style={{ padding: '2rem 1rem', textAlign: 'center' }}>
                <p style={S.muted}>Sin conversaciones</p>
              </div>
            )}
          </div>

          {/* Area de chat */}
          <div style={S.chatArea}>
            {!selected ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: '2rem' }}>💬</div>
                <p style={S.muted}>Selecciona un contacto para ver el chat</p>
              </div>
            ) : (
              <>
                {/* Header */}
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #2d2926', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ color: '#f2ead8', fontWeight: 600, fontSize: '.9rem' }}>
                      {leads.find(l => l.phone === selected)?.waName || '+' + selected}
                    </div>
                    <div style={{ fontSize: '.7rem', color: '#6a6050' }}>+{selected}</div>
                  </div>
                  {session && (
                    <span style={S.badge(session.canSendFreeText ? 'green' : 'amber')}>
                      {session.canSendFreeText
                        ? `Sesion activa (${session.hoursRemaining}h)`
                        : 'Sesion expirada (24h)'}
                    </span>
                  )}
                </div>

                {/* Mensajes */}
                <div style={S.chatMsgs}>
                  {loadingMsgs ? (
                    <p style={{ ...S.muted, textAlign: 'center', marginTop: '2rem' }}>Cargando mensajes...</p>
                  ) : messages.length === 0 ? (
                    <p style={{ ...S.muted, textAlign: 'center', marginTop: '2rem' }}>Sin mensajes</p>
                  ) : (
                    messages.map(m => (
                      <div key={m.id}>
                        <div style={S.bubble(m.direction === 'outbound')}>
                          {m.body}
                        </div>
                        <div style={{ ...S.bubbleTs, textAlign: m.direction === 'outbound' ? 'right' : 'left' }}>
                          {fmtTime(m.timestamp)}
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={msgsEndRef} />
                </div>

                {/* Input o locked */}
                {session?.canSendFreeText ? (
                  <div style={S.chatInput}>
                    <input
                      style={{ ...S.input, flex: 1 }}
                      placeholder="Escribe un mensaje..."
                      value={msgInput}
                      onChange={e => setMsgInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
                      disabled={sending}
                    />
                    <button style={S.btn('green')} onClick={handleSend} disabled={sending}>
                      {sending ? '...' : 'Enviar'}
                    </button>
                  </div>
                ) : (
                  <div style={S.locked}>
                    🔒 Han pasado mas de 24h desde el ultimo mensaje del cliente. No se puede enviar texto libre.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Modal: Convertir lead ────────────────────────────────── */}
      {convertPhone && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={e => { if (e.target === e.currentTarget) setConvertPhone(null); }}>
          <div style={{ background: '#1e1b17', border: '1px solid #2d2926', borderRadius: 12, padding: '2rem', width: '100%', maxWidth: 420 }}>
            <h3 style={{ color: '#f2ead8', fontSize: '1.1rem', marginBottom: '1rem' }}>Anadir como Cliente</h3>
            <p style={{ ...S.muted, marginBottom: '1.5rem' }}>Telefono: +{convertPhone}</p>

            <div style={{ marginBottom: '1rem' }}>
              <label style={S.label}>Nombre</label>
              <input style={S.input} value={convertName} onChange={e => setConvertName(e.target.value)} placeholder="Nombre del cliente" />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={S.label}>Email</label>
              <input style={S.input} value={convertEmail} onChange={e => setConvertEmail(e.target.value)} placeholder="email@ejemplo.com" />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={S.label}>Notas</label>
              <input style={S.input} value={convertNotes} onChange={e => setConvertNotes(e.target.value)} placeholder="Notas opcionales" />
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button style={S.btn()} onClick={() => setConvertPhone(null)}>Cancelar</button>
              <button style={S.btn('green')} onClick={handleConvert} disabled={converting}>
                {converting ? 'Creando...' : 'Crear Cliente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
